import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { App } from "@capacitor/app";
import { supabase } from "../lib/supabase";
import { notGradedMissingIds } from "../lib/not-graded-games";
import { transformRow } from "./useGameCacheRealtime";

/**
 * useNotGradedGames -- #1655 B3b, design amendment A1 (docs/audits/2026-10-31-1655-b3b-plan.md).
 *
 * Single job: load the FINAL/OFF `game_cache` rows the 3-day realtime window no longer holds,
 * for unsettled bets with a not-graded-league leg, so their NOT GRADED identity and final score
 * persist after the game ages out of the window.
 *
 * Reads: `game_cache` (`select *`, `id IN chunk`, `status IN (FINAL, OFF)`), the same RLS-public
 * table and grants as the window seed; rows go through the seed's own `transformRow`.
 * Writes: nothing (React state only).
 * Does NOT: subscribe to Realtime (a final row is immutable for display), fetch for nhl/mlb/nfl
 * (their legs never qualify, so the id set is empty and no request is made), or report to
 * Sentry/PostHog -- a failed read is retried at the next trigger and the bet falls back to
 * today's display, so it is a log line (CC-OBS-002, CC-IMPL-037).
 *
 * Re-fetches when the missing-id set changes (keyed on a STRING derived against the window
 * list, never the array -- D-P2-4) and on app foreground, throttled like the seed. A newer
 * request aborts the older one and a per-request sequence drops any late response; each read
 * has an abort timeout. On failure the last good rows are kept; rows whose id leaves the
 * current set are pruned (account switch).
 * Bounded: at most NOT_GRADED_MAX_IDS ids per load (the first of the sorted set, logged when
 * trimmed), in chunks of 100, with at most NOT_GRADED_MAX_PARALLEL requests in flight.
 */

export const NOT_GRADED_CHUNK_SIZE = 100;
export const NOT_GRADED_FETCH_TIMEOUT_MS = 15000;
// Pre-Codex fix 2: at most this many ids per load (the first ones of the sorted set, so the cut is
// deterministic; a log line says when it trims) and at most this many chunk requests in flight.
export const NOT_GRADED_MAX_IDS = 500;
export const NOT_GRADED_MAX_PARALLEL = 3;
const FOREGROUND_THROTTLE_MS = 15000;
const FINAL_STATUSES = ["FINAL", "OFF"];
const EMPTY = Object.freeze([]);

// The pure halves live in src/lib/not-graded-games.js (App.js imports the merge directly).
export { notGradedMissingIds, mergeNotGradedGames } from "../lib/not-graded-games";


// Run the tasks with at most `limit` in flight, results in task order. `stopped()` is checked
// before each start, so a superseded load starts no further requests.
async function runBounded(tasks, limit, stopped) {
  const results = new Array(tasks.length);
  let next = 0;
  const worker = async () => {
    while (next < tasks.length && !stopped()) {
      const i = next++;
      results[i] = await tasks[i]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return results;
}

function chunk(ids) {
  const out = [];
  for (let i = 0; i < ids.length; i += NOT_GRADED_CHUNK_SIZE) out.push(ids.slice(i, i + NOT_GRADED_CHUNK_SIZE));
  return out;
}

export function useNotGradedGames({ bets, games } = {}) {
  const key = useMemo(() => notGradedMissingIds(bets, games).join(","), [bets, games]);
  const [rows, setRows] = useState(EMPTY);
  const keyRef = useRef(key);
  keyRef.current = key;
  const seqRef = useRef(0);
  const controllerRef = useRef(null);
  const lastStartedAtRef = useRef(null);

  // Cancel whatever is in flight: its response can no longer write (the sequence moves on).
  const cancelInFlight = useCallback(() => {
    seqRef.current += 1;
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = null;
  }, []);

  const load = useCallback(async (keyStr) => {
    if (!supabase || !keyStr) return;
    cancelInFlight();
    const my = seqRef.current;
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    controllerRef.current = controller;
    lastStartedAtRef.current = Date.now();
    const timer = controller ? setTimeout(() => controller.abort(), NOT_GRADED_FETCH_TIMEOUT_MS) : null;
    let ids = keyStr.split(",");
    if (ids.length > NOT_GRADED_MAX_IDS) {
      console.warn("[useNotGradedGames] not_graded_games_id_cap", { total: ids.length, kept: NOT_GRADED_MAX_IDS });
      ids = ids.slice(0, NOT_GRADED_MAX_IDS);
    }
    let results;
    let thrown = null;
    try {
      const tasks = chunk(ids).map((part) => () => supabase
        .from("game_cache")
        .select("*")
        .in("id", part)
        .in("status", FINAL_STATUSES)
        .abortSignal(controller ? controller.signal : undefined));
      results = await runBounded(tasks, NOT_GRADED_MAX_PARALLEL, () => my !== seqRef.current);
    } catch (err) {
      thrown = err; // handled below with the error-result path (stale -> dropped, else a log line)
    } finally {
      if (timer) clearTimeout(timer);
    }
    // A newer request or an unmount superseded this one: it writes and logs nothing.
    if (my !== seqRef.current) return;
    controllerRef.current = null;
    const failed = thrown || (results || []).find((r) => !r || r.error || !Array.isArray(r.data));
    if (failed) {
      const error = thrown || failed?.error || null;
      console.warn("[useNotGradedGames] not_graded_games_fetch_failed", { code: error?.code ?? null, message: error?.message ?? "non-array data" });
      return; // keep the last good rows; the next trigger retries
    }
    const next = results
      .flatMap((r) => r.data)
      .map(transformRow)
      .filter((g) => g && FINAL_STATUSES.includes(g.status));
    setRows(next.length ? next : EMPTY);
  }, [cancelInFlight]);

  // One request per id-set change; the cleanup aborts it on a change or unmount.
  useEffect(() => {
    load(key);
    return cancelInFlight;
  }, [key, load, cancelInFlight]);

  // Foreground refresh, throttled like the window seed (a negative elapsed time is not throttled).
  useEffect(() => {
    if (!supabase) return undefined;
    let handle = null;
    let cancelled = false;
    (async () => {
      try {
        const h = await App.addListener("appStateChange", ({ isActive }) => {
          if (!isActive || !keyRef.current) return;
          const startedAt = lastStartedAtRef.current;
          const elapsed = startedAt == null ? Infinity : Date.now() - startedAt;
          if (elapsed >= 0 && elapsed < FOREGROUND_THROTTLE_MS) return;
          load(keyRef.current);
        });
        if (cancelled) { h.remove(); return; }
        handle = h;
      } catch (err) {
        // Degraded, not terminal: the id-set trigger still runs (log line, CC-OBS-002).
        console.warn("[useNotGradedGames] foreground listener unavailable", { message: err?.message ?? null });
      }
    })();
    return () => {
      cancelled = true;
      if (handle) handle.remove();
    };
  }, [load]);

  // Prune by the current id set: a row whose id left the set (settled, deleted, account switch)
  // is dropped at once, with no request.
  return useMemo(() => {
    if (!key || rows.length === 0) return EMPTY;
    const want = new Set(key.split(","));
    const kept = rows.filter((g) => want.has(String(g.id)));
    return kept.length === rows.length ? rows : (kept.length ? kept : EMPTY);
  }, [rows, key]);
}
